// src/components/tenants/TenantList.tsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
import TenantDetailModal from './TenantDetailModal';

const client = generateClient<Schema>();

export default function TenantList() {
    const queryClient = useQueryClient();
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
    const [viewingTenant, setViewingTenant] = useState<Schema['Tenant']['type'] | null>(null);

    const { data: tenants, isLoading, error } = useQuery({
        queryKey: ['tenants'],
        queryFn: async () => {
            const { data } = await client.models.Tenant.list();
            return [...data].sort((a, b) => a.name.localeCompare(b.name));
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (tenantId: string) => {
            const { errors } = await client.models.Tenant.delete({ id: tenantId });
            if (errors) {
                throw new Error(errors[0].message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenants'] });
            setConfirmDelete(null);
            setDeletingId(null);
        },
        onError: (error: any) => {
            console.error('Failed to delete tenant:', error);
            alert(`Failed to delete tenant: ${error.message}`);
            setDeletingId(null);
        },
    });

    const handleDelete = (tenantId: string) => {
        setDeletingId(tenantId);
        deleteMutation.mutate(tenantId);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800">Failed to load tenants: {error.message}</p>
            </div>
        );
    }

    return (
        <>
        {viewingTenant && (
            <TenantDetailModal
                tenant={viewingTenant}
                onClose={() => setViewingTenant(null)}
            />
        )}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-800">Managed Tenants</h2>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Domain
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Admin Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {tenants?.map((tenant) => (
                            <tr key={tenant.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                        {tenant.name}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-600">{tenant.domain}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-600">{tenant.adminEmail}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <StatusBadge status={tenant.status} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button
                                        onClick={() => setViewingTenant(tenant)}
                                        className="text-blue-600 hover:text-blue-900 mr-4"
                                    >
                                        View
                                    </button>
                                    {confirmDelete === tenant.id ? (
                                        <div className="inline-flex items-center space-x-2">
                                            <button
                                                onClick={() => handleDelete(tenant.id)}
                                                disabled={deletingId === tenant.id}
                                                className="text-red-600 hover:text-red-900 font-semibold disabled:opacity-50"
                                            >
                                                {deletingId === tenant.id ? 'Deleting...' : 'Confirm'}
                                            </button>
                                            <button
                                                onClick={() => setConfirmDelete(null)}
                                                disabled={deletingId === tenant.id}
                                                className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setConfirmDelete(tenant.id)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
        </>
    );
}

function StatusBadge({ status }: { status?: string }) {
    const styles = {
        ACTIVE: 'bg-green-100 text-green-800',
        PENDING: 'bg-yellow-100 text-yellow-800',
        INACTIVE: 'bg-gray-100 text-gray-800',
        SUSPENDED: 'bg-red-100 text-red-800',
    };

    return (
        <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${styles[status as keyof typeof styles] || styles.INACTIVE
                }`}
        >
            {status}
        </span>
    );
}